import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon, TrashIcon, PencilIcon, DocumentTextIcon, ClipboardDocumentListIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Card, CardContent } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { scriptService } from '../../services/scriptService';
import { campaignService } from '../../services/campaignService';
import { Campaign } from '../../types/campaign';
import { Script, ScriptStageBlock, ScriptStageChoice, ScriptStageNotes, ScriptStructure } from '../../types/script';

const makeId = () => crypto.randomUUID();
const slugify = (value: string) =>
  value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const blankNotes = (): ScriptStageNotes => ({ goal: '', avoid: '', handle: '', psychology: '' });
const blankStep = (id: string): ScriptStageBlock => ({ id, title: '', body: '', choices: [], notes: blankNotes() });
const CAMPAIGN_USAGE_PAGE_SIZE = 100;

const defaultStructure = (): ScriptStructure => ({
  opening: '',
  qualification: [],
  valuePitch: [],
  objections: [],
  close: '',
  followUp: '',
  compliance: [],
  stages: [],
  runtimeConfig: {
    openOn: 'connected',
    popupPosition: 'left-bottom',
    mode: 'read-only',
  },
});

const loadAllCampaignsForUsage = async (): Promise<Campaign[]> => {
  const campaigns: Campaign[] = [];
  let page = 1;
  let total = 0;

  do {
    const response = await campaignService.getCampaigns({
      page,
      limit: CAMPAIGN_USAGE_PAGE_SIZE,
    });
    const pageCampaigns = response.campaigns || [];

    campaigns.push(...pageCampaigns);
    total = response.total || campaigns.length;

    if (pageCampaigns.length === 0) break;
    page += 1;
  } while (campaigns.length < total);

  return campaigns;
};

const toGeneratedContent = (stages: ScriptStageBlock[]) =>
  stages
    .filter((s) => s.title.trim() || s.body.trim())
    .map((s) => `# ${s.title || s.id}\n${s.body}`)
    .join('\n\n')
    .trim();

const bulletLines = (value: string) =>
  String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const ScriptsPage: React.FC = () => {
  const editorRef = useRef<HTMLDivElement>(null);

  const [scripts, setScripts] = useState<Script[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const [editingScriptId, setEditingScriptId] = useState<string | null>(null);
  const [scriptName, setScriptName] = useState('');
  const [scriptDescription, setScriptDescription] = useState('');
  const [scriptActive, setScriptActive] = useState(true);
  const [steps, setSteps] = useState<ScriptStageBlock[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepViewHistory, setStepViewHistory] = useState<number[]>([]);
  const [editingScript, setEditingScript] = useState(false);
  const [stageDraft, setStageDraft] = useState('');
  const [lineDraft, setLineDraft] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState<ScriptStageNotes>(blankNotes());
  const [editingButtonModalIdx, setEditingButtonModalIdx] = useState<number | null>(null);
  const [buttonLabelDraft, setButtonLabelDraft] = useState('');

  const campaignUsageByScriptId = useMemo(() => {
    const map = new Map<string, Campaign[]>();
    campaigns.forEach((campaign) => {
      if (!campaign.scriptId) return;
      const list = map.get(campaign.scriptId) || [];
      list.push(campaign);
      map.set(campaign.scriptId, list);
    });
    return map;
  }, [campaigns]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [scriptRes, campaignRes] = await Promise.all([
        scriptService.getScripts(),
        loadAllCampaignsForUsage(),
      ]);
      setScripts(scriptRes);
      setCampaigns(campaignRes);
    } catch {
      toast.error('Failed to load scripts/campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNewEditor = () => {
    setEditingScriptId(null);
    setScriptName('');
    setScriptDescription('');
    setScriptActive(true);
    setSteps([]);
    setCurrentStepIndex(0);
    setStepViewHistory([]);
    setEditingScript(false);
    setEditingNotes(false);
    setEditingButtonModalIdx(null);
    setIsEditorOpen(true);
    setTimeout(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const openEditEditor = (script: Script) => {
    const source = (script.structure?.stages || []).map((stage) => ({
      ...stage,
      choices: stage.choices || [],
      notes: stage.notes || blankNotes(),
    }));
    setEditingScriptId(script.id);
    setScriptName(script.name);
    setScriptDescription(script.description || '');
    setScriptActive(script.isActive);
    setSteps(source);
    setCurrentStepIndex(0);
    setStepViewHistory([]);
    setEditingScript(false);
    setEditingNotes(false);
    setEditingButtonModalIdx(null);
    setIsEditorOpen(true);
    setTimeout(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingScriptId(null);
  };

  const currentStep = steps[currentStepIndex] ?? null;

  const startEditScript = () => {
    if (!currentStep) return;
    setStageDraft(currentStep.title);
    setLineDraft(currentStep.body);
    setEditingScript(true);
  };

  const saveScriptEdit = () => {
    setSteps((prev) =>
      prev.map((step, idx) =>
        idx === currentStepIndex ? { ...step, title: stageDraft.trim() || step.title, body: lineDraft } : step,
      ),
    );
    setEditingScript(false);
  };

  /** Merge in-progress step/notes drafts into steps before persisting (bottom Save). */
  const getStepsForPersist = (): ScriptStageBlock[] => {
    let next = steps;
    if (editingScript && currentStep) {
      next = next.map((step, idx) =>
        idx === currentStepIndex
          ? { ...step, title: stageDraft.trim() || step.title, body: lineDraft }
          : step,
      );
    }
    if (editingNotes && currentStep) {
      next = next.map((step, idx) => (idx === currentStepIndex ? { ...step, notes: notesDraft } : step));
    }
    return next;
  };

  const addButton = () => {
    if (!currentStep) return;
    const newStepId = `step-${Date.now()}`;
    const newChoice: ScriptStageChoice = { id: makeId(), label: 'New Reply', nextStageId: newStepId, color: 'slate' };
    const newStep = blankStep(newStepId);
    newStep.title = `Step ${steps.length + 1}`;
    setSteps((prev) => [
      ...prev.map((step, idx) =>
        idx === currentStepIndex ? { ...step, choices: [...(step.choices || []), newChoice] } : step,
      ),
      newStep,
    ]);
  };

  const deleteButton = (choiceIdx: number) => {
    setSteps((prev) =>
      prev.map((step, idx) =>
        idx === currentStepIndex
          ? { ...step, choices: (step.choices || []).filter((_, ci) => ci !== choiceIdx) }
          : step,
      ),
    );
  };

  const openButtonModal = (choiceIdx: number) => {
    const choice = steps[currentStepIndex]?.choices?.[choiceIdx];
    if (!choice) return;
    setButtonLabelDraft(choice.label);
    setEditingButtonModalIdx(choiceIdx);
  };

  const saveButtonModal = () => {
    if (editingButtonModalIdx === null) return;
    setSteps((prev) =>
      prev.map((step, idx) =>
        idx === currentStepIndex
          ? { ...step, choices: (step.choices || []).map((c, ci) => ci === editingButtonModalIdx ? { ...c, label: buttonLabelDraft } : c) }
          : step,
      ),
    );
    setEditingButtonModalIdx(null);
  };

  const deleteStep = (idx: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== idx));
    setCurrentStepIndex((cur) => Math.max(0, Math.min(cur, Math.max(0, steps.length - 2))));
    setEditingScript(false);
    setEditingNotes(false);
  };

  const addNewStep = () => {
    const newId = `step-${Date.now()}`;
    const newStep = blankStep(newId);
    const newIdx = steps.length;
    setSteps((prev) => [...prev, newStep]);
    setStepViewHistory((prev) => [...prev, currentStepIndex]);
    setCurrentStepIndex(newIdx);
    setStageDraft('');
    setLineDraft('');
    setEditingScript(true);
  };

  const startEditNotes = () => {
    if (!currentStep) return;
    setNotesDraft(currentStep.notes || blankNotes());
    setEditingNotes(true);
  };

  const saveNotesEdit = () => {
    setSteps((prev) =>
      prev.map((step, idx) => (idx === currentStepIndex ? { ...step, notes: notesDraft } : step)),
    );
    setEditingNotes(false);
  };

  const saveScript = async () => {
    if (!scriptName.trim()) {
      toast.error('Script name is required');
      return;
    }
    const stepsToSave = getStepsForPersist();
    if (!stepsToSave.length) {
      toast.error('Save at least one step before saving script');
      return;
    }
    // Keep local state in sync with what we persist (flush open drafts).
    if (editingScript || editingNotes) {
      setSteps(stepsToSave);
      setEditingScript(false);
      setEditingNotes(false);
    }
    const normalizedSteps = stepsToSave.map((step, idx) => ({
      ...step,
      id: step.id || `step-${idx + 1}`,
      title: step.title || `Step ${idx + 1}`,
      choices: step.choices || [],
      notes: step.notes || blankNotes(),
    }));
    const payload = {
      name: scriptName.trim(),
      description: scriptDescription.trim() || undefined,
      isActive: scriptActive,
      content: toGeneratedContent(normalizedSteps),
      structure: { ...defaultStructure(), stages: normalizedSteps },
    };

    setSaving(true);
    try {
      if (editingScriptId) {
        await scriptService.updateScript(editingScriptId, payload);
        toast.success('Script updated');
      } else {
        await scriptService.createScript(payload);
        toast.success('Script created');
      }
      await loadData();
      closeEditor();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'Failed to save script');
    } finally {
      setSaving(false);
    }
  };

  const deleteScript = async (id: string) => {
    if (!window.confirm('Delete this script?')) return;
    setDeletingId(id);
    try {
      await scriptService.deleteScript(id);
      toast.success('Script deleted');
      await loadData();
      if (editingScriptId === id) closeEditor();
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'Failed to delete script');
    } finally {
      setDeletingId(null);
    }
  };

  const activeCount = scripts.filter((s) => s.isActive).length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Scripts</h1>
          <p className="mt-1 text-sm text-muted-foreground">Create and manage call scripts, then assign them to campaigns.</p>
        </div>
        <Button onClick={openNewEditor} className="flex items-center gap-2 self-start sm:self-auto">
          <PlusIcon className="h-4 w-4" />
          Create Script
        </Button>
      </div>

      {/* Summary Stats */}
      {!loading && scripts.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-3 shadow-sm">
            <div className="text-xs font-medium text-muted-foreground">Total Scripts</div>
            <div className="mt-1 text-2xl font-bold text-foreground">{scripts.length}</div>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50/60 p-3 shadow-sm">
            <div className="text-xs font-medium text-green-700">Active</div>
            <div className="mt-1 text-2xl font-bold text-green-700">{activeCount}</div>
          </div>
          <div className="col-span-2 rounded-xl border border-border bg-card p-3 shadow-sm sm:col-span-1">
            <div className="text-xs font-medium text-muted-foreground">Inactive</div>
            <div className="mt-1 text-2xl font-bold text-foreground">{scripts.length - activeCount}</div>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-3 p-8 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading scripts...
            </div>
          ) : scripts.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <DocumentTextIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <div className="font-semibold text-foreground">No scripts yet</div>
                <div className="mt-1 text-sm text-muted-foreground">Create your first script to get started.</div>
              </div>
              <Button onClick={openNewEditor} className="mt-1 flex items-center gap-2">
                <PlusIcon className="h-4 w-4" />
                Create Script
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
              {scripts.map((script) => {
                const assignedCampaigns = campaignUsageByScriptId.get(script.id) || [];
                const stepCount = script.structure?.stages?.length ?? 0;
                const updatedDate = new Date(script.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                return (
                  <div
                    key={script.id}
                    className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition-all hover:shadow-md ${
                      script.isActive ? 'border-blue-200 dark:border-blue-800' : 'border-border'
                    }`}
                  >
                    {/* Accent bar */}
                    <div className={`h-1 w-full ${script.isActive ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-muted'}`} />

                    <div className="flex flex-1 flex-col p-4">
                      {/* Header row */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                            script.isActive ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-muted text-muted-foreground'
                          }`}>
                            <DocumentTextIcon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-foreground">{script.name}</div>
                            <div className="truncate text-xs text-muted-foreground">{script.description || 'No description'}</div>
                          </div>
                        </div>
                        <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          script.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-muted text-muted-foreground'
                        }`}>
                          {script.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      {/* Meta row */}
                      <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                          {stepCount} {stepCount === 1 ? 'step' : 'steps'}
                        </span>
                        <span className="text-border/60">•</span>
                        <span className="flex items-center gap-1">
                          <CalendarDaysIcon className="h-3.5 w-3.5" />
                          {updatedDate}
                        </span>
                      </div>

                      {/* Campaign badges */}
                      <div className="mt-3 flex-1">
                        {assignedCampaigns.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {assignedCampaigns.slice(0, 3).map((c) => (
                              <span key={c.id} className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                                {c.name}
                              </span>
                            ))}
                            {assignedCampaigns.length > 3 && (
                              <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                +{assignedCampaigns.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/70 italic">Not assigned to any campaign</span>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="mt-4 flex gap-2 border-t border-border pt-3">
                        <Button
                          size="sm"
                          variant="outlined"
                          onClick={() => openEditEditor(script)}
                          className="flex flex-1 items-center justify-center gap-1.5"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteScript(script.id)}
                          disabled={deletingId === script.id}
                          className="flex flex-1 items-center justify-center gap-1.5"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          {deletingId === script.id ? 'Deleting…' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {isEditorOpen && (
        <div ref={editorRef} className="space-y-5">
          {/* Script meta */}
          <div className="grid gap-3 sm:grid-cols-3">
            <input className="input" placeholder="Script name *" value={scriptName} onChange={(e) => setScriptName(e.target.value)} />
            <input className="input" placeholder="Description (optional)" value={scriptDescription} onChange={(e) => setScriptDescription(e.target.value)} />
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={scriptActive} onChange={(e) => setScriptActive(e.target.checked)} />
              Active
            </label>
          </div>

          {/* ── Main 2-col layout ── */}
          <div className="grid gap-5 lg:grid-cols-[1fr_360px]">

            {/* Left: Agent Script panel */}
            <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-xl dark:border-slate-700 dark:bg-slate-800/90">

              {/* Header */}
              <div className="mb-5 flex items-start justify-between gap-4">
                <div className="flex-1">
                  {!editingScript ? (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Agent Script</div>
                      <h2 className="mt-1 text-3xl font-bold text-slate-950 dark:text-slate-50">{currentStep?.title || <span className="text-slate-400 dark:text-slate-500">Untitled Step</span>}</h2>
                    </>
                  ) : (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Agent Script</div>
                      <input
                        value={stageDraft}
                        onChange={(e) => setStageDraft(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-blue-200 bg-white p-3 text-2xl font-bold outline-none focus:border-blue-400 dark:border-blue-700 dark:bg-slate-900 dark:text-slate-50 dark:placeholder-slate-500"
                        placeholder="Step title"
                        autoFocus
                      />
                    </>
                  )}
                </div>
                <div className="flex-shrink-0 rounded-full bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                  {steps.length > 0 ? `Step ${currentStepIndex + 1} of ${steps.length}` : '—'}
                </div>
              </div>

              {/* Script body */}
              <div className="min-h-[220px] rounded-3xl border border-blue-100 bg-gradient-to-br from-white to-blue-50/70 p-6 shadow-inner dark:border-blue-900/50 dark:from-slate-800 dark:to-blue-950/20">
                {!editingScript ? (
                  <div className="whitespace-pre-line text-xl leading-9 text-slate-800 dark:text-slate-200">
                    {currentStep?.body || <span className="italic text-slate-400 dark:text-slate-500">No script text yet. Click "Edit Script" to start.</span>}
                  </div>
                ) : (
                  <textarea
                    value={lineDraft}
                    onChange={(e) => setLineDraft(e.target.value)}
                    className="min-h-[190px] w-full resize-none rounded-2xl border border-blue-100 bg-white p-4 text-lg leading-8 outline-none focus:border-blue-400 dark:border-blue-900/50 dark:bg-slate-900 dark:text-slate-200 dark:placeholder-slate-600"
                    placeholder="Script line..."
                  />
                )}
              </div>

              {/* Edit / Save / Add / Delete buttons */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {!editingScript ? (
                  <>
                    <button
                      onClick={startEditScript}
                      disabled={!currentStep}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2 font-semibold shadow-sm hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                    >
                      Edit Script
                    </button>
                    <button
                      onClick={addNewStep}
                      className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 font-semibold shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Step
                    </button>
                    {currentStep && (
                      <button
                        onClick={() => deleteStep(currentStepIndex)}
                        className="ml-auto rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400 dark:hover:bg-red-950/70"
                      >
                        Delete Step
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button onClick={saveScriptEdit} className="rounded-xl bg-blue-700 px-4 py-2 font-semibold text-white shadow-sm hover:bg-blue-800">
                      Apply Step
                    </button>
                    <button onClick={() => setEditingScript(false)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 font-semibold shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600">
                      Cancel
                    </button>
                  </>
                )}
              </div>

              {/* Merchant Reply Buttons */}
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-600 dark:text-slate-400">Merchant Reply Buttons</div>
                  <button
                    onClick={addButton}
                    disabled={!currentStep}
                    className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                  >
                    + Add Button
                  </button>
                </div>

                {(!currentStep || (currentStep.choices || []).length === 0) ? (
                  <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400 dark:border-slate-600 dark:text-slate-500">
                    No reply buttons yet. Click "+ Add Button" to add one.
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(currentStep.choices || []).map((choice, choiceIdx) => {
                      const targetIdx = steps.findIndex((s) => s.id === choice.nextStageId);
                      return (
                        <div key={choice.id} className="rounded-2xl border border-blue-100 bg-white/70 p-2 shadow-sm dark:border-slate-600 dark:bg-slate-700/60">
                          <button
                            onClick={() => {
                              if (targetIdx >= 0) {
                                setStepViewHistory((prev) => [...prev, currentStepIndex]);
                                setCurrentStepIndex(targetIdx);
                                setEditingScript(false);
                                setEditingNotes(false);
                              }
                            }}
                            className="group w-full rounded-xl bg-slate-950 px-5 py-4 text-left font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-xl active:translate-y-0"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span>{choice.label || 'New Button'}</span>
                              <span className="opacity-70 transition group-hover:translate-x-1">→</span>
                            </div>
                          </button>
                          <div className="mt-2 flex items-center gap-2">
                            <button onClick={() => openButtonModal(choiceIdx)} className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold hover:bg-slate-50 dark:border-slate-500 dark:bg-slate-600 dark:text-slate-200 dark:hover:bg-slate-500">
                              Edit
                            </button>
                            <button onClick={() => deleteButton(choiceIdx)} className="rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700">
                              Delete
                            </button>
                            <div className="ml-auto truncate text-[10px] text-slate-500 dark:text-slate-400">
                              → {targetIdx >= 0 ? (steps[targetIdx].title || choice.nextStageId) : <span className="text-red-400 dark:text-red-400">not linked</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Navigation: Previous + dots */}
              <div className="mt-7 flex items-center justify-between gap-3 border-t border-slate-100 pt-4 dark:border-slate-700">
                <button
                  onClick={() => {
                    setStepViewHistory((prev) => {
                      if (!prev.length) return prev;
                      const last = prev[prev.length - 1];
                      setCurrentStepIndex(last);
                      setEditingScript(false);
                      setEditingNotes(false);
                      return prev.slice(0, -1);
                    });
                  }}
                  disabled={stepViewHistory.length === 0}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1.5">
                  {steps.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setStepViewHistory((prev) => [...prev, currentStepIndex]);
                        setCurrentStepIndex(idx);
                        setEditingScript(false);
                        setEditingNotes(false);
                      }}
                      className={`h-2 rounded-full transition-all ${idx === currentStepIndex ? 'w-6 bg-blue-600' : 'w-2 bg-slate-300 hover:bg-slate-400 dark:bg-slate-600 dark:hover:bg-slate-500'}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Rules / Importance panel */}
            <div className="custom-scroll h-fit max-h-[80vh] overflow-y-auto rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-xl dark:border-slate-700 dark:bg-slate-800/90">
              <div className="mb-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Rules / Importance</div>
                <h2 className="mt-1 text-2xl font-bold text-slate-950 dark:text-slate-50">{currentStep?.title || 'Untitled Step'}</h2>
              </div>

              {!editingNotes ? (
                <div className="space-y-4 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <div className="rounded-2xl border border-blue-100 bg-white/80 p-4 shadow-sm dark:border-blue-900/50 dark:bg-slate-700/60">
                    <div className="mb-1 font-bold text-slate-950 dark:text-slate-100">Goal of this stage</div>
                    <ul className="list-disc space-y-1 pl-5">
                      {bulletLines(currentStep?.notes?.goal || '').filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
                      {!currentStep?.notes?.goal?.trim() && <li className="italic opacity-40">No goal added yet</li>}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-red-100 bg-red-50/70 p-4 shadow-sm dark:border-red-900/50 dark:bg-red-950/20">
                    <div className="mb-1 font-bold text-red-700 dark:text-red-400">What to avoid</div>
                    <ul className="list-disc space-y-1 pl-5">
                      {bulletLines(currentStep?.notes?.avoid || '').filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
                      {!currentStep?.notes?.avoid?.trim() && <li className="italic opacity-40">No avoid notes yet</li>}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-green-100 bg-green-50/70 p-4 shadow-sm dark:border-green-900/50 dark:bg-green-950/20">
                    <div className="mb-1 font-bold text-green-700 dark:text-green-400">How to handle</div>
                    <ul className="list-disc space-y-1 pl-5">
                      {bulletLines(currentStep?.notes?.handle || '').filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
                      {!currentStep?.notes?.handle?.trim() && <li className="italic opacity-40">No handling notes yet</li>}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 shadow-sm dark:border-indigo-900/50 dark:bg-indigo-950/20">
                    <div className="mb-1 font-bold text-indigo-700 dark:text-indigo-400">Psychology</div>
                    <ul className="list-disc space-y-1 pl-5">
                      {bulletLines(currentStep?.notes?.psychology || '').filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
                      {!currentStep?.notes?.psychology?.trim() && <li className="italic opacity-40">No psychology notes yet</li>}
                    </ul>
                  </div>
                  <button
                    onClick={startEditNotes}
                    disabled={!currentStep}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 font-semibold hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                  >
                    Edit Notes
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <textarea value={notesDraft.goal} onChange={(e) => setNotesDraft({ ...notesDraft, goal: e.target.value })} className="textarea w-full" rows={3} placeholder="Goal (one item per line)" />
                  <textarea value={notesDraft.avoid} onChange={(e) => setNotesDraft({ ...notesDraft, avoid: e.target.value })} className="textarea w-full" rows={3} placeholder="What to avoid (one item per line)" />
                  <textarea value={notesDraft.handle} onChange={(e) => setNotesDraft({ ...notesDraft, handle: e.target.value })} className="textarea w-full" rows={3} placeholder="How to handle (one item per line)" />
                  <textarea value={notesDraft.psychology} onChange={(e) => setNotesDraft({ ...notesDraft, psychology: e.target.value })} className="textarea w-full" rows={3} placeholder="Psychology (one item per line)" />
                  <div className="flex gap-2">
                    <button onClick={saveNotesEdit} className="flex-1 rounded-xl bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800">Save</button>
                    <button onClick={() => setEditingNotes(false)} className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2 font-semibold hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* All Steps navigator */}
          {steps.length > 0 && (
            <div>
              <div className="mb-2 text-sm font-semibold text-foreground">All Steps ({steps.length})</div>
              <div className="space-y-1.5">
                {steps.map((step, idx) => (
                  <div
                    key={`${step.id}-${idx}`}
                    onClick={() => { setStepViewHistory((prev) => [...prev, currentStepIndex]); setCurrentStepIndex(idx); setEditingScript(false); setEditingNotes(false); }}
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-2.5 transition-colors ${idx === currentStepIndex ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20' : 'border-border hover:bg-muted/30'}`}
                  >
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-foreground">{step.title || `Step ${idx + 1}`}</div>
                      <div className="text-xs text-muted-foreground">ID: {step.id} · {(step.choices || []).length} button{(step.choices || []).length !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="destructive" onClick={() => deleteStep(idx)}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Save / Cancel ── */}
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="outlined" onClick={closeEditor} disabled={saving}>Cancel</Button>
            <Button onClick={saveScript} disabled={saving}>
              {saving ? 'Saving…' : editingScriptId ? 'Save Script to Server' : 'Create Script'}
            </Button>
          </div>
        </div>
      )}

      {/* Button edit modal */}
      {editingButtonModalIdx !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
          onClick={() => setEditingButtonModalIdx(null)}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-slate-800" onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold text-slate-950 dark:text-slate-50">Edit Button</h2>
            <div className="space-y-3">
              <input
                value={buttonLabelDraft}
                onChange={(e) => setButtonLabelDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveButtonModal()}
                className="input w-full"
                placeholder="Button label"
                autoFocus
              />
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-700/60 dark:text-slate-400">
                Click the button itself in the main view to navigate to the linked step and edit it there.
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEditingButtonModalIdx(null)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 font-semibold hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600">Cancel</button>
                <button onClick={saveButtonModal} className="rounded-xl bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800">Save Button</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .custom-scroll {
          scrollbar-width: thin;
          scrollbar-color: #3b82f6 transparent;
        }
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 9999px; }
      `}</style>
    </div>
  );
};

export default ScriptsPage;
